/**
 * azure-functions/shared/controlOwnerResolution.cjs
 *
 * Canonical implementation. Called directly by Azure Functions (via require)
 * and re-exported for the frontend by src/utils/controlOwnerResolution.js —
 * there is exactly one copy of this logic, the same arrangement
 * emailIdentity.cjs uses, and for the same reason: the portal routes work to
 * people on the server, and the app reports ownership gaps in the browser, off
 * one answer.
 *
 * The one place that answers: given `state.controlOwnership[controlId].ownerId`,
 * WHICH REAL, CURRENT PERSON is that — and can they be reached in the employee
 * portal?
 *
 * `ownerId` is a **user** id. Employees and users are two separate populations
 * with no stored foreign key; they join on canonical email only, via
 * `emailIdentity.js` (IDN-B-003). That join was written for display purposes and
 * has two properties that are fine for a name label and unacceptable for routing
 * compliance work to a human being:
 *
 *   • `findEmployeeByEmail` returns the FIRST match, and
 *     `buildEmployeeEmailIndex` keeps the first and silently drops the rest
 *     (`if (key && !map.has(key))`). Duplicate canonical emails are a normal real
 *     state — a rehire, a contractor record beside an employee record, an
 *     un-archived leaver. Under a portal that delivers work, WHICH HUMAN receives
 *     the magic link and answers the compliance question would be decided by
 *     position in an array.
 *   • Neither filters on employment status, so a departed person's record can win
 *     the join. `minimum-requirements/employees.json`'s own `mustNeverClaim` is
 *     "That a responsibility belongs to someone who no longer holds the relevant
 *     role" — routing a control owner's obligation to a leaver and rendering it as
 *     assigned is that claim, made by derivation.
 *
 * So this module never returns a bare person or null. It returns a discriminated
 * result, and everything that is not `resolved` must be reported as UNASSIGNED by
 * every caller — IDN-B-004 inverted: unknown is never reported as covered.
 *
 * Multiplicity rule, stated once so nothing re-derives it: candidates are first
 * narrowed to people who are CURRENTLY EMPLOYED. Exactly one survivor resolves.
 * Two or more survivors is `ambiguous` — refused and surfaced, never picked. This
 * is a rule, not a positional guess: a rehire whose old record is marked departed
 * resolves cleanly to the current record, while two live records for one address
 * is a data problem a human has to settle before work is sent to either of them.
 */

const { canonicaliseEmail } = require('./emailIdentity.cjs');
const { realEmployees } = require('./sampleRecords.cjs');

/**
 * Employment statuses that mean the person no longer holds their role.
 *
 * Same rule as `resolveReaders.js`'s `activeEmployees()`, which excludes both
 * from policy acknowledgement. An offboarding person is on their way out; giving
 * them a recurring governance obligation produces a task nobody will answer, so
 * both statuses are treated as "re-assign this" rather than only `departed`. The
 * result carries `employeeStatus` so a surface can say which it was.
 */
const NON_CURRENT_STATUSES = new Set(['departed', 'offboarding']);

/** True iff this employee record represents someone who currently holds their role. */
function isCurrentlyEmployed(employee) {
  if (!employee) return false;
  return !NON_CURRENT_STATUSES.has(employee.status);
}

/**
 * Reads the owner id for a control, honouring the legacy-id / TCF-id duality that
 * every other ownership reader uses (`controlOwnership[control.id]` falling back
 * to `controlOwnership[scfId]`).
 *
 * @param {object} controlOwnership  state.controlOwnership
 * @param {string} controlId         the control's own id
 * @param {string} [scfId]           its resolved TCF id, when different
 * @returns {string|null}
 */
function readOwnerId(controlOwnership, controlId, scfId) {
  const map = controlOwnership || {};
  const record = map[controlId] || (scfId ? map[scfId] : null) || null;
  return record?.ownerId || null;
}

/**
 * Resolve a control owner to a current person reachable in the employee portal.
 *
 * @param {object} args
 * @param {string|null} args.ownerId       from state.controlOwnership
 * @param {Array}  args.users              tenant users (from tenant_users)
 * @param {Array}  args.employees          state.employees
 * @param {Array}  [args.tenantDomains]    state.tenantDomains
 * @returns {{status: string, [k: string]: any}} one of:
 *   { status: 'resolved', ownerId, user, employee, employeeId, personName, email }
 *   { status: 'no-owner' }
 *   { status: 'unknown-owner', ownerId }
 *   { status: 'ambiguous', ownerId, email, candidateIds }
 *   { status: 'departed', ownerId, email, employeeId, employeeStatus }
 *   { status: 'no-portal-identity', ownerId, email }
 */
function resolveControlOwner({ ownerId, users, employees, tenantDomains } = {}) {
  if (!ownerId) return { status: 'no-owner' };

  const user = (users || []).find((u) => u && u.id === ownerId) || null;
  if (!user) return { status: 'unknown-owner', ownerId };

  const canonical = canonicaliseEmail(user.email, tenantDomains);
  if (!canonical) return { status: 'no-portal-identity', ownerId, email: user.email || null };

  // A sample person is never an accountable owner (GH #7870): an owner who
  // matches only a sample record resolves as having no portal identity.
  const matches = realEmployees(employees || []).filter(
    (emp) => canonicaliseEmail(emp && emp.email, tenantDomains) === canonical
  );
  if (matches.length === 0) {
    return { status: 'no-portal-identity', ownerId, email: user.email };
  }

  const current = matches.filter(isCurrentlyEmployed);

  if (current.length === 0) {
    // Every record on this address belongs to someone who has left. Naming one is
    // safe here because the answer is the same whichever it is: re-assign.
    const latest = matches[matches.length - 1];
    return {
      status: 'departed',
      ownerId,
      email: user.email,
      employeeId: latest?.id ?? null,
      employeeStatus: latest?.status ?? null,
    };
  }

  if (current.length > 1) {
    return {
      status: 'ambiguous',
      ownerId,
      email: user.email,
      candidateIds: current.map((e) => e.id),
    };
  }

  const employee = current[0];
  return {
    status: 'resolved',
    ownerId,
    user,
    employee,
    employeeId: employee.id,
    personName: employee.name || user.name || user.displayName || user.email,
    email: user.email,
  };
}

/**
 * The predicate every caller uses instead of re-deriving "can work be sent here?".
 * Anything that is not `resolved` renders as UNASSIGNED, everywhere — including
 * any "obligations with an owner" count a compliance lead reads. The failure this
 * exists to forbid is the split reader: the portal drops the row because no person
 * resolves, while the lead's screen still counts it as covered.
 */
function isAssignableOwner(resolution) {
  return !!resolution && resolution.status === 'resolved';
}

/**
 * Maps a resolution status to the ownership-gap id that reports it.
 *
 * `unknown-owner` deliberately maps to the pre-existing `'owner-missing'` gap id
 * rather than a new one — that key is live in tenant-facing surfaces and must not
 * be renamed.
 */
const OWNER_RESOLUTION_GAP = {
  'no-owner': 'unowned',
  'unknown-owner': 'owner-missing',
  ambiguous: 'owner-ambiguous',
  departed: 'owner-departed',
  'no-portal-identity': 'owner-no-portal-identity',
};

/** Every gap id that describes a problem with WHO owns a control, rather than with the control's work. */
const OWNER_IDENTITY_GAPS = new Set(Object.values(OWNER_RESOLUTION_GAP));

module.exports = {
  isCurrentlyEmployed,
  readOwnerId,
  resolveControlOwner,
  isAssignableOwner,
  OWNER_RESOLUTION_GAP,
  OWNER_IDENTITY_GAPS,
};
